/**
 * `memberapp/calendar/calendarChips.ts` — the chip vocabulary of the desk
 * calendar (#2554): what each event on a day is called, its kind (which sets
 * its tint) and its icon.
 *
 *   seat      sun or moon, "55 AM"   `accent-soft`, ink `fg`, no edge bar
 *   leave     plane, "Leave"         `danger-soft`
 *   sick      heart-pulse            `danger-soft`
 *   rest      cup, "Rest day"        `warning-soft`
 *   released  sun or moon, dashed and struck (a seat released for cover)
 *   personal  no icon, outline
 *
 * The sun or moon never guesses an hour (the `windowOf` contract): a seat
 * with no derived time window carries neither. A moon is a window that
 * starts at or after 17:00 or ends after midnight.
 *
 * The colours live in `deskCalendar.css`, on the `data-chip` attribute; this
 * file holds only the data, so the Month cards, the day detail and the Week
 * and Day lanes say the same thing.
 */
import { Coffee, HeartPulse, type LucideIcon, Moon, Plane, Square, Sun } from "lucide-react";

import {
  type AbsenceSpan,
  type DayEvents,
  type DerivedWindow,
  absenceChip,
  seatBarLabel,
  windowOf,
} from "./calendarModel";

export type ChipKind = "seat" | "leave" | "sick" | "rest" | "released" | "personal";
export type ChipIconName = "sun" | "moon" | "plane" | "heart-pulse" | "cup" | "square";

export const CHIP_ICON: Record<ChipIconName, LucideIcon> = {
  sun: Sun,
  moon: Moon,
  plane: Plane,
  "heart-pulse": HeartPulse,
  cup: Coffee,
  square: Square,
};

/** A window that starts at or after this minute reads as night. */
const NIGHT_FROM_MINUTE = 17 * 60;
const MINUTES_PER_DAY = 24 * 60;

/** Sun or moon for a derived window; none when the seat has no window. */
export function seatIconOf(window: DerivedWindow | null): "sun" | "moon" | null {
  if (!window) return null;
  const night = window.startMinute >= NIGHT_FROM_MINUTE || window.endMinute > MINUTES_PER_DAY;
  return night ? "moon" : "sun";
}

/** Leave, off sick or a rest day (a given-back rest day is stored as leave). */
export function absenceChipKind(span: AbsenceSpan): "leave" | "sick" | "rest" {
  if (span.kind === "mc") return "sick";
  if (span.restAfterRecallIso) return "rest";
  return "leave";
}

export const ABSENCE_ICON: Record<"leave" | "sick" | "rest", ChipIconName> = {
  leave: "plane",
  sick: "heart-pulse",
  rest: "cup",
};

export interface DayChip {
  key: string;
  kind: ChipKind;
  label: string;
  icon: ChipIconName | null;
  /** A moon: the night tint. */
  night: boolean;
}

/**
 * The chips of one day, in reading order: absences, then seats, then
 * personal blocks (the order the old bars used).
 */
export function dayChips(events: DayEvents | undefined): DayChip[] {
  if (!events) return [];
  const chips: DayChip[] = [];
  for (const a of events.absences) {
    const kind = absenceChipKind(a);
    chips.push({
      key: `abs-${a.kind}-${a.id ?? a.startIso}`,
      kind,
      label: absenceChip(a),
      icon: ABSENCE_ICON[kind],
      night: false,
    });
  }
  for (const s of events.shifts) {
    const icon = seatIconOf(windowOf(s));
    chips.push({
      key: s.assignment_id ?? `${s.date}-${s.role}-${s.team_id}`,
      // #1909 (finding 33) — released for cover on an MC day.
      kind: s.released_for_cover ? "released" : "seat",
      label: seatBarLabel(s.role),
      icon,
      night: icon === "moon",
    });
  }
  for (const t of events.timeblocks) {
    chips.push({
      key: `tb-${t.block.id}`,
      kind: "personal",
      label: t.block.title.trim() || "Personal",
      icon: null,
      night: false,
    });
  }
  return chips;
}

/** The icon a chip shows when it is small: a personal block is an outline square. */
export function smallIconOf(chip: DayChip): ChipIconName {
  return chip.icon ?? "square";
}
